import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Table, TableColumnConfig, TableProps, Text } from '@frontend/design-system';

const DEFAULT_PAGE_SIZE = 10;
const TABLE_ROWS_MAX_HEIGHT = 520;

export interface LocationComparisonTableProps<T extends object> {
  /** The array of row data for the current page. */
  data: T[];
  /** Column configuration for the metric columns (Location column is added automatically). */
  colConfig: TableColumnConfig<T>[];
  /** Whether the table is in a loading state. */
  isLoading: boolean;
  /** A unique identifier for this table instance (used for persisting column visibility, order, etc.). */
  tableInstanceId: string;
  /** Whether there is a next page of data available. */
  hasNextPage: boolean;
  /** The current pagination state. */
  pageConfig: { pageNumber: number; pageSize: number };
  /** Callback fired when the user clicks next/prev page. */
  onPageChange: (action: 'next' | 'prev') => void;
  /** Callback fired when the user changes the rows-per-page value. */
  onPageSizeChange: (size: number) => void;
  /** Available page-size options in the rows-per-page selector. */
  rowsPerPageOptions?: number[];
  /**
   * The key in the data object that holds the location name.
   * Defaults to `'key'`.
   */
  locationAccessor?: string;
  /**
   * Custom header label for the Location column.
   * Defaults to `'Location'`.
   */
  locationHeader?: string;

  // Optional: Whether to show the global search input. Defaults to true.
  hasGlobalSearch?: boolean;
}

/**
 * A reusable, generic table component for location comparison views across
 * analytics features. Wraps the design-system `<Table>` with a standard
 * layout (filterable & resizable columns, manual pagination, global search).
 *
 * The sticky "Location" column is built in automatically — consumers only
 * need to provide the metric columns via `colConfig`.
 */
export const LocationComparisonTable = <T extends object>({
  data,
  colConfig,
  isLoading,
  tableInstanceId,
  hasNextPage,
  pageConfig,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions = [10, 25, 50, 75, 100],
  locationAccessor = 'key',
  locationHeader = 'Location',
  hasGlobalSearch = true,
}: LocationComparisonTableProps<T>) => {
  // Built-in sticky Location column — always the first column
  const locationColumn = {
    id: 'locationName',
    Header: locationHeader,
    accessor: locationAccessor,
    disableSortBy: true,
    minWidth: 240,
    cellRenderer: (cellValue: string) => (
      <div css={styles.locationCell}>
        <Text weight='bold'>{cellValue}</Text>
      </div>
    ),
    sticky: 'left' as const,
  } as TableColumnConfig<T>;

  const fullColConfig: TableColumnConfig<T>[] = [locationColumn, ...colConfig];

  const tableProps: TableProps<T> = {
    isLoading,
    data,
    tableInstanceId,
    hasFilterColumns: true,
    hasResizeColumns: true,
    hasGlobalSearch,
    colConfig: fullColConfig,
    fullHeight: false,
    wrapperStyle: css`
      .table__body {
        max-height: ${TABLE_ROWS_MAX_HEIGHT}px;
      }
    `,
    isPaginated: true,
    manualPaginationConfig: {
      handleChange: onPageChange,
      hasNext: hasNextPage,
      hasPrevious: pageConfig.pageNumber > 1,
      page: pageConfig.pageNumber,
      defaultRowsPerPage: pageConfig.pageSize,
      onNumRowsChange: onPageSizeChange,
      rowsPerPageOptions,
    },
  };

  return <Table {...tableProps} />;
};

LocationComparisonTable.defaultPageSize = DEFAULT_PAGE_SIZE;

const styles = {
  locationCell: css`
    border-right: 1px solid ${theme.colors.neutral10};
    width: 100%;
    height: 100%;
    margin: ${theme.spacing(-1, -2)};
    padding: ${theme.spacing(1, 2)};
    box-sizing: content-box;
    display: flex;
    align-items: center;
  `,
};
